import './modules/header';
import './modules/contact-modal';
import './modules/organization';
import './modules/organization-slider';
import './modules/organization-text';
import './modules/previous-events';
import './modules/partner-testimonials';
import './modules/upcoming-events';
import './modules/strategic-mentorship-slider';
import './modules/faq';
import './modules/events-archive';
import './modules/smooth-scroll';
import './modules/home-animations';
import './modules/page-animations';

const blogSingle = document.querySelector('.blog-single');

if (blogSingle) {
  const shareButton = blogSingle.querySelector('[data-share-url]');
  const shareLabel = blogSingle.querySelector('[data-share-label]');
  const shareIcon = blogSingle.querySelector('[data-share-icon]');
  const fontToggleButton = blogSingle.querySelector('[data-blog-font-toggle]');
  const audioToggleButton = blogSingle.querySelector('[data-blog-audio-toggle]');
  const audioIcon = blogSingle.querySelector('[data-blog-audio-icon]');
  const contentBody = blogSingle.querySelector('.blog-single__content-body');

  const fontScaleKey = 'rv-blog-single-font-scale';
  const fontScales = [1, 1.12, 1.24];
  const fontScaleLabels = ['100%', '112%', '124%'];
  let currentFontScaleIndex = 0;
  const tocLinks = Array.from(blogSingle.querySelectorAll('.blog-single__toc-link'));
  const tocHeadingTargets = Array.from(contentBody.querySelectorAll('h2[id]'));

  const setFontScale = (index) => {
    currentFontScaleIndex = index;
    const fontScale = fontScales[currentFontScaleIndex];
    blogSingle.style.setProperty('--blog-single-scale', String(fontScale));

    if (fontToggleButton) {
      fontToggleButton.setAttribute('aria-label', `Tamanho da fonte: ${fontScaleLabels[currentFontScaleIndex]}`);
    }

    try {
      localStorage.setItem(fontScaleKey, String(fontScale));
    } catch (error) {
      // Persistência opcional.
    }
  };

  try {
    const storedScale = Number.parseFloat(localStorage.getItem(fontScaleKey) || '');
    const storedIndex = fontScales.findIndex((scale) => Math.abs(scale - storedScale) < 0.001);

    if (storedIndex >= 0) {
      setFontScale(storedIndex);
    } else {
      setFontScale(0);
    }
  } catch (error) {
    setFontScale(0);
  }

  if (fontToggleButton) {
    fontToggleButton.addEventListener('click', () => {
      const nextIndex = (currentFontScaleIndex + 1) % fontScales.length;
      setFontScale(nextIndex);
    });
  }

  const setActiveTocLink = (activeId) => {
    tocLinks.forEach((link) => {
      const linkTarget = (link.getAttribute('href') || '').replace('#', '');
      const isActive = linkTarget === activeId;
      link.classList.toggle('is-active', isActive);
      link.setAttribute('aria-current', isActive ? 'true' : 'false');
    });
  };

  if (tocLinks.length && tocHeadingTargets.length) {
    let currentActiveHeadingId = tocHeadingTargets[0].id;
    setActiveTocLink(currentActiveHeadingId);

    const headingObserver = new IntersectionObserver(
      (entries) => {
        const visibleEntries = entries
          .filter((entry) => entry.isIntersecting)
          .sort((firstEntry, secondEntry) => secondEntry.intersectionRatio - firstEntry.intersectionRatio);

        if (!visibleEntries.length) {
          return;
        }

        currentActiveHeadingId = visibleEntries[0].target.id;
        setActiveTocLink(currentActiveHeadingId);
      },
      {
        root: null,
        rootMargin: '-140px 0px -60% 0px',
        threshold: [0, 0.15, 0.3, 0.5, 0.75, 1],
      }
    );

    tocHeadingTargets.forEach((heading) => headingObserver.observe(heading));

    window.addEventListener('scroll', () => {
      const activeHeading = [...tocHeadingTargets]
        .reverse()
        .find((heading) => heading.getBoundingClientRect().top <= 180);

      if (activeHeading && activeHeading.id !== currentActiveHeadingId) {
        currentActiveHeadingId = activeHeading.id;
        setActiveTocLink(currentActiveHeadingId);
      }
    }, { passive: true });
  }

  const resetAudioButton = () => {
    if (!audioToggleButton) {
      return;
    }

    audioToggleButton.classList.remove('is-playing');
    audioToggleButton.setAttribute('aria-pressed', 'false');
    audioToggleButton.setAttribute('aria-label', 'Ouvir post');

    if (audioIcon) {
      const playSrc = audioIcon.getAttribute('data-play-src');
      if (playSrc) {
        audioIcon.setAttribute('src', playSrc);
      }
    }

    const label = audioToggleButton.querySelector('span:last-child');
    if (label) {
      label.textContent = 'Ouvir post';
    }
  };

  const setAudioButtonState = (isPlaying) => {
    if (!audioToggleButton) {
      return;
    }

    audioToggleButton.classList.toggle('is-playing', isPlaying);
    audioToggleButton.setAttribute('aria-pressed', isPlaying ? 'true' : 'false');
    audioToggleButton.setAttribute('aria-label', isPlaying ? 'Parar leitura' : 'Ouvir post');

    if (audioIcon) {
      const nextSrc = isPlaying
        ? audioIcon.getAttribute('data-pause-src')
        : audioIcon.getAttribute('data-play-src');
      if (nextSrc) {
        audioIcon.setAttribute('src', nextSrc);
      }
    }

    const label = audioToggleButton.querySelector('span:last-child');
    if (label) {
      label.textContent = isPlaying ? 'Parar leitura' : 'Ouvir post';
    }
  };

  if (audioToggleButton && contentBody && 'speechSynthesis' in window && 'SpeechSynthesisUtterance' in window) {
    let activeUtterance = null;
    let queuedTimeout = null;
    let stopRequested = false;

    const stopSpeaking = () => {
      stopRequested = true;

      if (queuedTimeout) {
        window.clearTimeout(queuedTimeout);
        queuedTimeout = null;
      }

      window.speechSynthesis.cancel();
      activeUtterance = null;
      resetAudioButton();
    };

    const splitIntoChunks = (text) => {
      const normalizedText = (text || '')
        .replace(/\u00a0/g, ' ')
        .replace(/\r\n/g, '\n')
        .replace(/\n{3,}/g, '\n\n')
        .trim();

      if (!normalizedText) {
        return [];
      }

      const blocks = normalizedText
        .split(/\n{2,}/)
        .map((block) => block.replace(/\s+/g, ' ').trim())
        .filter(Boolean);

      const chunks = [];
      const segmenter = 'Segmenter' in Intl
        ? new Intl.Segmenter('pt-BR', { granularity: 'sentence' })
        : null;

      blocks.forEach((block) => {
        if (segmenter) {
          const sentences = Array.from(segmenter.segment(block), ({ segment }) => segment.trim()).filter(Boolean);
          sentences.forEach((sentence) => {
            chunks.push(sentence);
          });
          return;
        }

        chunks.push(
          ...block
            .split(/(?<=[.!?…])\s+/)
            .map((sentence) => sentence.trim())
            .filter(Boolean)
        );
      });

      return chunks;
    };

    const getChunkPause = (chunk) => {
      if (/\.\.\.$/.test(chunk)) {
        return 560;
      }

      if (/[.!?…]$/.test(chunk)) {
        return 420;
      }

      if (/[:;]$/.test(chunk)) {
        return 300;
      }

      if (/[,–-]$/.test(chunk)) {
        return 220;
      }

      return chunk.length > 140 ? 240 : 160;
    };

    const getPreferredVoice = () => {
      const voices = window.speechSynthesis.getVoices();
      return voices.find((voice) => voice.lang === 'pt-BR')
        || voices.find((voice) => (voice.lang || '').toLowerCase().startsWith('pt'))
        || voices.find((voice) => voice.localService)
        || voices[0]
        || null;
    };

    const speakChunkAt = (chunks, index) => {
      if (stopRequested || index >= chunks.length) {
        stopSpeaking();
        stopRequested = false;
        return;
      }

      const chunk = chunks[index];
      const utterance = new SpeechSynthesisUtterance(chunk);
      utterance.lang = 'pt-BR';
      utterance.rate = 0.82;
      utterance.pitch = 0.88;
      utterance.volume = 1;

      const preferredVoice = getPreferredVoice();
      if (preferredVoice) {
        utterance.voice = preferredVoice;
      }

      utterance.onstart = () => {
        setAudioButtonState(true);
      };

      utterance.onend = () => {
        if (stopRequested) {
          return;
        }

        const delay = getChunkPause(chunk);
        queuedTimeout = window.setTimeout(() => {
          speakChunkAt(chunks, index + 1);
        }, delay);
      };

      utterance.onerror = () => {
        stopSpeaking();
        stopRequested = false;
      };

      activeUtterance = utterance;
      window.speechSynthesis.speak(utterance);
    };

    audioToggleButton.addEventListener('click', () => {
      const isCurrentlySpeaking = window.speechSynthesis.speaking || window.speechSynthesis.pending;

      if (isCurrentlySpeaking) {
        stopSpeaking();
        stopRequested = false;
        return;
      }

      const chunks = splitIntoChunks(contentBody.innerText || '');
      if (!chunks.length) {
        return;
      }

      stopSpeaking();
      stopRequested = false;
      speakChunkAt(chunks, 0);
    });

    window.addEventListener('pagehide', stopSpeaking);
    window.addEventListener('beforeunload', stopSpeaking);
  } else if (audioToggleButton) {
    audioToggleButton.disabled = true;
    audioToggleButton.setAttribute('aria-disabled', 'true');
    audioToggleButton.setAttribute('aria-label', 'Leitura em voz alta indisponível neste navegador');
  }

  if (shareButton) {
    let shareFeedbackTimeout = null;

    const setShareState = (state) => {
      if (shareLabel) {
        shareLabel.textContent = state === 'success' ? 'Copiado' : 'Compartilhar';
      }

      if (shareIcon) {
        const nextSrc = state === 'success'
          ? shareIcon.getAttribute('data-share-success-src')
          : shareIcon.getAttribute('data-share-default-src');
        if (nextSrc) {
          shareIcon.setAttribute('src', nextSrc);
        }
      }

      if (shareFeedbackTimeout) {
        window.clearTimeout(shareFeedbackTimeout);
      }

      shareFeedbackTimeout = window.setTimeout(() => {
        if (shareLabel) {
          shareLabel.textContent = 'Compartilhar';
        }

        if (shareIcon) {
          const defaultSrc = shareIcon.getAttribute('data-share-default-src');
          if (defaultSrc) {
            shareIcon.setAttribute('src', defaultSrc);
          }
        }
      }, 1800);
    };

    shareButton.addEventListener('click', async () => {
      const shareUrl = shareButton.getAttribute('data-share-url');

      if (!shareUrl) {
        return;
      }

      if (navigator.clipboard && window.isSecureContext) {
        try {
          await navigator.clipboard.writeText(shareUrl);
          setShareState('success');
          return;
        } catch (error) {
          // Continua para o fallback manual abaixo.
        }
      }

      const temporaryInput = document.createElement('input');
      temporaryInput.value = shareUrl;
      temporaryInput.setAttribute('readonly', '');
      temporaryInput.style.position = 'absolute';
      temporaryInput.style.left = '-9999px';
      document.body.appendChild(temporaryInput);
      temporaryInput.select();

      try {
        document.execCommand('copy');
        document.body.removeChild(temporaryInput);
        setShareState('success');
        return;
      } catch (error) {
        document.body.removeChild(temporaryInput);
      }

      setShareState('error');
    });
  }
}
